import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { getRulesFromState, rulesCache, get, haveRulesChanged, pageContext } from 'ctm-util-functions';
import styles from './modal_container.css';
import { Modal } from './modal';
import { ModalCloseIcon } from './modal_close_icon/modal_close_icon';

/**
 * A generic modal container that formats, prepares and passes props to our visual modal component
 */
class _ModalContainer extends Component {

    /**
     * Set up the modal state
     * @param {Object} props - the component props
     */
    constructor(props) {
        super(props);

        this.state = {
            isOpen: get(props, 'stateProps.isOpen', false),
            scrollY: 0
        };
    }

    /**
     * As the component receives new props we need to check visibility rules to toggle visibility accordingly
     * @param {Object} nextProps - the new props to be received
     */
    componentWillReceiveProps = (nextProps) => {
        const { rules, dispatch } = this.props;
        if (rules && rules.isOpen) {
            if (nextProps.rules.isOpen.result !== rules.isOpen.result) {
                this.checkisOpenRule(nextProps.rules);
            }
        }

        if (rules && rules.validModalActions) {
            // This is used when we have a form in the modal. In these cases we need to do something with the form values
            // when they are submitted. The submission happens via a button on the modal, which will validate the form
            // and close the modal while setting the validModalActions rule to true.
            // We catch this here ONCE when the rule result between the current and next props has changed and dispatch
            // each fo the actions defined in the rule's props.
            const currentValidModalRule = rules.validModalActions;
            const nextValidModalRule = nextProps.rules.validModalActions;
            if (nextValidModalRule.result !== currentValidModalRule.result && nextValidModalRule.result === true) {
                const elementRule = rulesCache.getElementRuleForId(currentValidModalRule.id);
                elementRule.props.actions.map((action) => dispatch(action));
                // If the modal has validModalActions, then these actions should include the logic for closing the
                // modal.
                // In this case, we need to explicitly set state.isOpen to false, and then call toggleBodyStyle()
                // to ensure that we remove the overflow: hidden on the body, otherwise any scrollbars that should
                // be displayed will be missing.
                // This is normally handled when the user explicitly closes the modal by clicking the overlay or the
                // close button
                this.setState({ isOpen: false }, (prevState, props) => this.toggleBodyStyle());
            }
        }
    };

    /**
     * Only update the component if we're newly on the xs breakpoint or there's a new rule result
     * The incoming rules here are coming from the nextProps via destructuring
     * @param {Object} nextProps - The incoming set of props
     * @param {Object} nextState - The incoming state
     * @returns {Boolean} whether or not to update the component
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        const { xs } = this.props;
        const { isOpen } = this.state;
        return (haveRulesChanged(nextProps, this.props)
            || (nextProps.xs !== xs))
            || (nextState.isOpen !== isOpen);
    };

    /**
     * As the component mounts we need to check visibility rules to toggle visibility accordingly
     */
    componentDidMount = () => {
        const { rules } = this.props;
        this.checkisOpenRule(rules);
    };

    /**
     * Check the component's visibility rules and toggle the visibility of the modal depending on the result
     * @param {Object} rules - the rule types and results (we're on concerned with visibility)
     */
    checkisOpenRule = (rules) => {
        if (rules && rules.isOpen) {
            this.toggleVisibility(rules.isOpen.result);
        }
    };

    /**
     * Based on a boolean value, trigger the modal open or closed (true: open, false: closed)
     * @param {bool} visible - the results of the visibility rules evaluation
     */
    toggleVisibility = (visible) => {
        visible ? this.openModal() : this.closeModal();
    };

    /**
     * The onClick event handler for the modals overlay div (the white transparent full page overlay)
     * @param {Object} event - the event handler object
     */
    onOverlayClick = (event) => {
        const { eventProps = {} } = this.props;
        const allowClose = get(eventProps, 'allowCloseOnOverlayClick', false);
        if (event.target.id === 'overlay' && allowClose) this.closeModal();
    };

    /**
     * Given an array of schema defined 'event handlers' (objects with a type and action), iterate and fire one by one
     * @param {Array} eventHandlers - an array of object defined actions and functions to execute on particular events
     */
    fireModalEvents = (eventHandlers) => {
        // in case we haven't specified onOpen or onClose events within our schema return
        if (!eventHandlers) return;

        const { dispatch } = this.props;
        // for each action defined for a modal event, trigger the correct action
        eventHandlers.forEach((handler) => {
            switch (handler.type) {
                case 'action':
                    dispatch(handler.action);
                    break;
                case 'function':
                    if (typeof handler.action === 'function') {
                        handler.action(handler.args);
                    }
                    // here if we ever need it. This would have to be similar to an actions file where we create custom functions.
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.error(`No matching event handler for type '${handler.type}`);
            }
        });
    };

    /**
     * Callback function for open/close modal function to prevent body scrolling
     * Will cause an issue if more than one modal is open
     */
    toggleBodyStyle = () => {
        const { xs } = this.props;
        const { isOpen, scrollY } = this.state;
        if (window) {
            const bodyClassName = document.body.className;
            const modalBodyStyles = ` ${styles.body}`;
            const bodyStyles = bodyClassName.includes(modalBodyStyles) ? bodyClassName.replace(modalBodyStyles, '') : bodyClassName;
            // Store last scrolled position
            if (xs && window.scrollY !== 0) {
                this.setState({ scrollY: window.scrollY });
            }
            document.body.className = isOpen ? `${bodyClassName} ${styles.body}` : bodyStyles;
            // Add last scroll position to body when modal is closed
            if (xs && !isOpen && scrollY !== 0) {
                document.body.scrollTop = scrollY;
            }
        }
    };

    /**
     * Fires onOpen events and switches the modals isOpen state to true - to reveal the modal
     */
    openModal = () => {
        this.fireModalEvents(get(this.props, 'eventProps.onOpen'));
        this.setState({ isOpen: true }, this.toggleBodyStyle);
        this.toggleModal(true);
    };

    /**
     * Fires onClose events and switches the modals isOpen state to false - to hide the modal
     */
    closeModal = () => {
        this.fireModalEvents(get(this.props, 'eventProps.onClose'));
        this.setState({ isOpen: false }, this.toggleBodyStyle);
        this.toggleModal(false);
    };

    /**
     * Fires a dispatch which sets the isModalOpen value to the store
     * @param {bool} isOpen whether the modal is open or not
     */
    toggleModal = (isOpen) => {
        const { dispatch } = this.props;
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'isModalOpen',
            value: isOpen
        });
    };

    /**
     * Render the close icon with the "closeIcon" specific props, as well as the onClickFunction which gets added to event props in the component
     * @param {Object} closeIcon The closeIcon props, if you need to override them by dispatching a
     * @returns {ModalCloseIcon} A close icon component.
     */
    getCloseIcon = (closeIcon = {}) => {
        if (!closeIcon) {
            return null;
        }
        const closeIconProps = Object.assign(closeIcon, { onClickFunction: this.closeModal });
        return React.createElement(ModalCloseIcon, closeIconProps);
    };

    /**
     * Build components for the modal
     * @param {Object} components The component properties
     * @param {Function} onClickFunction The method to call - either openModal or closeModal
     * @returns {null} Built components
     */
    buildComponents = (components, onClickFunction) => {
        const { stateProps } = this.props;
        return components && components.length ? pageContext.getProps().director.constructComponents(components, { onClickFunction, stateProps }) : null;
    };

    /**
     * Pass the prepared props, state, and children to the visual modal component
     * @returns {Node} the visual modal component
     */
    render = () => {
        const { isOpen } = this.state;
        const {
            rules,
            xs,
            openComponent,
            headerComponents,
            footerComponents,
            closeIcon,
            visualProps,
            children
        } = this.props;

        if (!get(rules, 'visibility.result', true)) {
            return null;
        }
        return (
            <Modal
                stateProps={{
                    isOpen,
                    xs
                }}
                contentProps={{
                    openComponent: this.buildComponents(openComponent, this.openModal),
                    headerComponents: this.buildComponents(headerComponents, this.closeModal),
                    footerComponents: this.buildComponents(footerComponents, this.closeModal),
                    closeIcon: this.getCloseIcon(closeIcon)
                }}
                eventProps={{
                    onOverlayClick: this.onOverlayClick
                }}
                visualProps={visualProps}
            >
                {children}
            </Modal>
        );
    };

}

_ModalContainer.propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    stateProps: PropTypes.shape({
        isOpen: PropTypes.bool
    }),
    eventProps: PropTypes.shape({
        onOpen: PropTypes.arrayOf(PropTypes.object),
        onClose: PropTypes.arrayOf(PropTypes.object),
        allowCloseOnOverlayClick: PropTypes.bool
    }),
    /**
      * An array of components e.g. ["component_someComponent"]
      */
    openComponent: PropTypes.array,
    /**
      * An array of components that get rendered in the header section. If this is empty, it will not show a header.
      */
    headerComponents: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.bool
    ]),
    /**
      * An array of components that get rendered in the footer section. If this is empty, it will not show a footer.
      */
    footerComponents: PropTypes.array,
    visualProps: PropTypes.shape({
        // These are the available variants defined in the modal.css file.
        overlayVariant: PropTypes.oneOf(['dark', 'light']),
        outerVariant: PropTypes.oneOf(['centered', 'full_width_xs_centered_sm', 'centered_large']),
        containerVariant: PropTypes.oneOf(['centered', 'full_width_xs_centered_sm', 'large']),
        contentVariant: PropTypes.oneOf(['padded', 'gapless'])
    }),
    rules: PropTypes.shape({
        isOpen: PropTypes.shape({
            result: PropTypes.bool
        }),
        visibility: PropTypes.shape({
            result: PropTypes.bool
        }),
        validModalActions: PropTypes.object
    }),
    dispatch: PropTypes.func,
    closeIcon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.bool
    ]),
    xs: PropTypes.bool,
    // eslint-disable-next-line react/no-unused-prop-types
    buttonRules: PropTypes.object,
    children: PropTypes.node
};

/**
 * Map rules to our local component props
 * @param {Object} state - the application state
 * @param {object } props - the component props
 * @returns {{rules: *}} - the results of rule evaluation
 */
const mapStateToProps = (state, props) => ({
    rules: getRulesFromState(state, props),
    xs: state.get('browser').mediaType === 'xs'
});

const ModalContainer = connect(mapStateToProps)(_ModalContainer);
export { ModalContainer, _ModalContainer };
